Vue
<template>
  <div class="locks-view fill-height">
    <v-toolbar color="black" dark>
      <v-toolbar-title>Locks</v-toolbar-title>
      <v-chip v-if="isNative" :color="getStatusColor(syncStatus)" text-color="white" small label>
        {{ syncStatus }}
      </v-chip>
      <v-spacer></v-spacer>
      <v-btn :loading="loading" @click="resync" :disabled="!isNative || !bleReady" color="primary" class="ml-2">
        <v-icon start>mdi-reload</v-icon>
        Resync
      </v-btn>
    </v-toolbar>

    <v-container class="px-4 pt-2">
      <v-row align="center" no-gutters>
        <v-col cols="auto">

          <v-chip v-if="isSynced" label size="large" prepend-icon="mdi-key" variant="elevated" style="margin-top:-18px">
            Locks: {{ locks.length }}
          </v-chip>
        </v-col>
        <v-col v-if="isSynced">
          <v-text-field v-model="search" variant="solo-inverted" prepend-inner-icon="mdi-magnify"
            label="Search locks..." clearable density="comfortable" class="ml-4" />
        </v-col>
      </v-row>
    </v-container>
    <v-container v-if="!isSynced" class="fill-height d-flex flex-column justify-center align-center">
      <v-progress-circular indeterminate color="primary" size="64" width="6" />
      <div class="mt-4 text-h6 font-weight-medium">Please Wait...</div>
    </v-container>

    <v-container v-if="!isNative" class="fill-height d-flex justify-center align-center">
      <v-card class="pa-4 rounded-lg" max-width="360" elevation="6" density="compact"
        title="Bluetooth Not Supported on Web" subtitle="This feature requires mobile device capabilities."
        text="Please install the mobile app to unlock locks via Bluetooth." />
    </v-container>

    <v-container v-else-if="!bleReady" class="fill-height d-flex justify-center align-center">
      <v-card class="pa-4 rounded-lg" max-width="360" elevation="6" density="compact" title="Bluetooth Not Ready"
        subtitle="Please turn on Bluetooth to continue." text="Tap the button below to activate Bluetooth.">
        <v-card-actions class="d-flex flex-column align-stretch mt-3">
          <v-btn color="primary" block :loading="loading" @click="enable">
            Enable Bluetooth
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>

    <v-container v-else-if="isSynced" class="pa-4">
      <v-alert v-if="!locks.length" type="info" border="start" color="blue" text>
        No doors found nearby.
      </v-alert>
      <v-row dense>
        <v-col v-for="lock in filteredLocks" :key="lock.id" cols="12" class="mb-2">
          <v-card class="d-flex align-center pa-3" outlined>
            <div class="flex-grow-1">
              <div class="font-weight-medium">{{ lock?.configs?.name || 'Unnamed Lock' }}</div>
              <div class="text-caption text-grey-darken-1">{{ lock?.floor?.name || 'No Floor' }}</div>
            </div>
            <v-btn color="black" icon class="ml-3" :aria-label="`Unlock ${lock?.configs?.name}`" @click="unlock(lock)">
              <v-icon>mdi-key</v-icon>
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { BleClient } from '@capacitor-community/bluetooth-le'
import Swal from 'sweetalert2'
import moment from 'moment'

import { initBluetooth, isBleSupported } from '@/services/helpers/BluetoothHelper'
import { loadLocksHelper, showToast } from '@/services/helpers/LocksViewHelperOLD'
import { isIos, isAndroid } from '@/services/OAuthService'
import { syncAccessControlDeviceId, unregisterDeviceAccessControl } from '@/controllers/BackboneAPI'
import { loadConfig } from '@/configLoader'

const isNative = ref(false)
const bleReady = ref(false)
const loading = ref(false)
const locks = ref([])
const syncStatus = ref('Checking...')
const isSynced = ref(false)
const search = ref('')
let AccessPlugin = null

const LAST_SYNC_KEY = 'blueid_last_sync'
const DEVICE_ID = 'blueid_device_id'
const CACHED_LOCKS_KEY = 'cached_locks'

const getStatusColor = status => {
  if (status.includes('Synced')) return 'green'
  if (status.includes('failed') || status.includes('❌')) return 'red'
  if (status.includes('Syncing')) return 'orange'
  return 'grey'
}

const setup = async () => {
  isNative.value = Capacitor.isNativePlatform()
  if (!isNative.value) return

  syncStatus.value = 'Setting up...'
  showToast('Setting up Bluetooth...', 'info')
  bleReady.value = await initBluetooth({ openSettingsIfFail: false })
  if (!bleReady.value) {
    syncStatus.value = 'Bluetooth off'
    showToast('Could not enable Bluetooth', 'error')
    return
  }



  const ios = await isIos()
  const android = await isAndroid()

  if (ios) {
    AccessPlugin = (await import('@/plugins/ios/BlueIdIosHelperPlugin.js')).default
    showToast('iPhone access system ready', 'success')
  } else if (android) {
    AccessPlugin = (await import('@/plugins/android/blueIdHelperPlugin.js')).BlueId
    showToast('Android access system ready', 'success')
  } else {
    syncStatus.value = 'Unsupported'
    showToast('Device not supported for unlocking', 'warning')
    return
  }
  const { isInitialized } = await AccessPlugin.isInitialized()
  if (!isInitialized) {
    syncStatus.value = 'Initializing'
    showToast('Getting access ready again...', 'info')
    const config = await loadConfig()
    const { deviceId } = await AccessPlugin.initialize({ apiKey: config.accessControl.apiKey })
    Preferences.set({ key: DEVICE_ID, value: deviceId })
    await syncAccessControlDeviceId(deviceId)
    await Preferences.set({ key: 'blueid_device_id', value: deviceId })
    showToast('Re-initialized and registered', 'success')
  } else {
    syncStatus.value = 'Ready'
    showToast('SDK already initialized', 'info')
  }
  const cached = await Preferences.get({ key: CACHED_LOCKS_KEY })
  if (cached?.value) {
    try {
      const parsed = JSON.parse(cached.value)
      if (Array.isArray(parsed)) {
        locks.value = parsed
        syncStatus.value = 'Using cached locks'
        showToast('Loaded locks from cache', 'info', 3000)
      }
    } catch (e) {
      console.warn('⚠️ Failed to parse cached locks', e)
    }
  }

  syncStatus.value = 'Scanning...'
  showToast('Scanning for locks...', 'info', 3000)
  const loadedLocks = await loadLocksHelper()
  if (!loadedLocks.length) {
    syncStatus.value = 'No locks'
    showToast('No locks found nearby', 'warning', 3000)
    return
  }
  locks.value = loadedLocks

  try {
    const localSync = await Preferences.get({ key: LAST_SYNC_KEY })
    const lastLocalSync = localSync.value ? moment(localSync.value) : null

    if (lastLocalSync && moment().diff(lastLocalSync, 'days') < 4) {
      syncStatus.value = 'Synced'
      isSynced.value = true
      loading.value = false
      showToast('Locks recently synced from storage', 'info', 3000)
      return
    }

    syncStatus.value = 'Syncing...'
    showToast('Syncing locks...', 'info', 3000)

    const result = await retrySync(locks.value)

    if (result?.securedObjects?.length || isSynced.value) {
      syncStatus.value = 'Synced'
      isSynced.value = true
      await Preferences.set({ key: LAST_SYNC_KEY, value: moment().toISOString() })
      loading.value = false
    } else {
      syncStatus.value = 'Sync failed'
      syncStatus.value === showToast('No locks found during sync', 'error')
      loading.value = false
    }

  } catch (e) {
    syncStatus.value = 'Setup failed'
    console.error('❌ Setup failed:', e)
    showToast('Could not set up access system', 'error')
    loading.value = false
  }
}

onMounted(setup)

const resync = async () => {
  loading.value = true
  syncStatus.value = 'Resetting...'
  isSynced.value = false
  showToast('Resetting access system...', 'info')

  try {
    Preferences.remove({ key: LAST_SYNC_KEY })
    Preferences.remove({ key: CACHED_LOCKS_KEY })
    const cahceDeviceId = await Preferences.get({ key: DEVICE_ID })
    await unregisterDeviceAccessControl(cahceDeviceId.value)
    const { deviceId } = await AccessPlugin.getDeviceId()
    await unregisterDeviceAccessControl(deviceId)
    showToast('Unregistered device ID', 'info')
  } catch (err) {
    console.error('❌ Unregister failed:', err)
    showToast('Failed to unregister device ID', 'error')
  }

  try {
    console.log('🧹 Destroying session...')
    if (!(await AccessPlugin.isInitialized())?.isInitialized) {
      console.warn('Skip destroy: Not initialized')
      return setup()
    }
    await AccessPlugin.destroy()
    showToast('Session destroyed. Reinitializing...', 'info')
  } catch (err) {
    console.error('❌ Destroy failed:', err)
    showToast('Failed to destroy session', 'error')
    await new Promise(resolve => setTimeout(resolve, 3000))
    return setup()
  }

  await new Promise(resolve => setTimeout(resolve, 3000))
  return setup()

}


const unlock = async lock => {
  try {
    const bleOk = await isBleSupported()
    const isBluetoothEnabled = await BleClient.isEnabled()
    if (!bleOk) {
      Swal.fire({ icon: 'warning', title: 'BLE Not Supported', text: 'Bluetooth LE is not supported or enabled on this device.' })
      return
    }
    if (!isBluetoothEnabled) return BleClient.requestEnable()

    Swal.fire({
      title: '<div style="font-size: 24px; font-weight: bold; color: black; border-radius: 25px; padding: 8px 20px; background-color: #f0f0f0;">Unlocking...</div>',
      html: `
        <div style="font-size: 18px; color: black; background: white; padding: 20px; border-radius: 10px; display: flex; flex-direction: column; align-items: center; box-shadow: 0 4px 10px rgba(0,0,0,0.1);">
          <img src="${require('@/assets/General/lock.gif')}" alt="Loading Lock" style="width: 80px; height: 80px; margin-bottom: 10px;" />
          <p style="margin: 0;">Please wait while we attempt to unlock.</p>
        </div>`,
      allowOutsideClick: false,
      showConfirmButton: false,
    })

    const blueIdLock = JSON.parse(JSON.stringify(lock))
    const blueIdObjId = blueIdLock?.blueIdObjectId
    console.log(`🔑 Unlocking door with BlueID Object ID: ${blueIdObjId}`)
    const action = 'tokn'
    const result = await AccessPlugin.runCommand({ id: blueIdObjId, action })
    console.log(`🔓 Unlock result:`, result)

    if (result !== undefined) {
      Swal.fire({
        icon: 'success',
        title: '<div style="font-size: 24px; font-weight: bold;">Access Granted!</div>',
        html: `<p style="font-size: 16px; color: black; margin: 0;">Lock opened successfully!</p>`,
        showConfirmButton: false,
        timer: 2000,
      })
    } else {
      Swal.fire({
        icon: 'error',
        title: '<div style="font-size: 24px; font-weight: bold;">Access Denied!</div>',
        html: `<p style="font-size: 16px; color: black; margin: 0;">Please try again.</p>`,
        showConfirmButton: false,
        timer: 2000,
      })
    }
  } catch (error) {
    console.error('Failed to unlock:', error)
    if (error?.message?.toLowerCase().includes('no matching device') || error?.message === 'Command execution failed: BlueIDErrorRemoteDeviceNotFound') {
      Swal.fire({
        icon: 'warning',
        title: '<div style="font-size: 24px; font-weight: bold;">Not Nearby!</div>',
        html: `<p style="font-size: 16px; color: black; margin: 0;">Connection is not establishing. Try again.</p>` +
          `<style>.swal2-confirm { background-color: #81c784 !important; color: #fff !important; border: none !important; box-shadow: none !important; } .swal2-confirm:focus { box-shadow: 0 0 0 2px #388e3c !important; }</style>`,
        showConfirmButton: true,
        customClass: { confirmButton: '' }
      })
      return
    }
    Swal.fire({
      icon: 'error',
      title: '<div style="font-size: 24px; font-weight: bold;">Unlock Failed!</div>',
      html: `<p style="font-size: 16px; color: black; margin: 0;">An unexpected error occurred.</p>`,
      showConfirmButton: false,
      timer: 2000,
    })
  }
}

const retrySync = async apiLocks => {
  let attempts = 0
  const maxAttempts = 10
  let securedObjects = []

  for (attempts = 1; attempts <= maxAttempts; attempts++) {
    syncStatus.value = `Syncing (${attempts}/${maxAttempts})`
    console.log(`🔁 Attempt ${attempts} of ${maxAttempts} – Syncing locks...`)
    if (attempts === 1 || attempts === maxAttempts) showToast(`Attempt ${attempts}: syncing locks...`, 'info', 3000)

    const syncResult = await AccessPlugin.synchronize()
    securedObjects = syncResult.securedObjects || []

    const syncedCount = securedObjects.length
    const localCount = apiLocks.length
    console.log(`📊 Synced ${syncedCount} / ${localCount} locks`)
    showToast(`Synced ${syncedCount} / ${localCount} locks`, syncedCount >= localCount - 5 ? 'success' : 'warning', 3000)

    if (syncedCount >= localCount - 5) {
      syncStatus.value = `Synced at ${moment().format('HH:mm')}`
      isSynced.value = true
      console.log(`✅ Sync completed on attempt ${attempts}`)
      showToast('Sync successful', 'success')
      await Preferences.set({ key: LAST_SYNC_KEY, value: moment().toISOString() })
      await Preferences.set({ key: CACHED_LOCKS_KEY, value: JSON.stringify(securedObjects) })
      loading.value = false
      return { securedObjects }
    }
    await new Promise(resolve => setTimeout(resolve, 4000))
  }

  console.warn('❌ Sync failed after 10 attempts — fallback starting')
  showToast('Sync failed after multiple attempts', 'error')
  syncStatus.value = 'Sync failed'
  await new Promise(resolve => setTimeout(resolve, 3000))
  try {
    syncStatus.value = 'Syncing'
    showToast('Syncing locks again...', 'info')
    return resync()
  } catch (error) {
    console.error('❌ Resync failed:', error)
    showToast('Resync failed', 'error')
    Preferences.remove({ key: LAST_SYNC_KEY })
    Preferences.remove({ key: CACHED_LOCKS_KEY })
    Preferences.remove({ key: DEVICE_ID })
    Swal.fire({
      title: '<div style="font-size: 22px; font-weight: bold;">Refreshing Page</div>',
      html: `
          <div style="font-size: 16px; color: black; margin-top: 10px;">
            <b>1</b> <span style="margin: 0 8px;">→</span> <b>2</b>
            <br/>
            <v-progress-circular indeterminate color="primary" size="48" width="5"></v-progress-circular>
          </div>
        `,
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      }
    })

  }

}

const enable = async () => {
  loading.value = true
  showToast('Turning on Bluetooth...', 'info')
  bleReady.value = await initBluetooth()
  if (bleReady.value) {
    showToast('Updating nearby locks...', 'info')
    return setup()
  } else {
    showToast('Bluetooth still off', 'error')
  }
  loading.value = false
}
const filteredLocks = computed(() => {
  if (!search.value) return locks.value
  return locks.value.filter(lock => {
    const name = lock?.configs?.name || ''
    const floor = lock?.floor?.name || ''
    return name.toLowerCase().includes(search.value.toLowerCase()) ||
      floor.toLowerCase().includes(search.value.toLowerCase())
  })
})

</script>

<style scoped>
.fill-height {
  height: 100%;
}
</style>
